import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { apiUrl } from 'src/constants/config';
import ReduxService from 'src/redux/redux.service';
import { ReduxState } from 'src/redux/redux-state';
import {
  ReportingApiErrorEnvelope,
  ReportingApiReportQuery,
  ReportingApiRow,
  ReportingApiSuccessEnvelope,
  ReportingApiV1FlightRow,
  ReportingApiV1FlightsRequest,
} from './reporting-api-v1.types';

/** Derive `/api/v1` from `VITE_API_URL` which ends in `/v1`. */
export const getReportingApiV1BaseUrl = (): string => {
  const trimmed = (apiUrl || '').replace(/\/+$/, '');
  if (trimmed.endsWith('/v1')) {
    return `${trimmed.slice(0, -3)}/api/v1`;
  }
  return `${trimmed}/api/v1`;
};

const getAccessToken = (): string | undefined => {
  const state = ReduxService.Store.getState() as ReduxState;
  return state?.userState?.access || undefined;
};

export type ReportingApiV1RequestError = {
  status?: number;
  message: string;
  code?: string;
};

const toRequestError = (error: unknown): ReportingApiV1RequestError => {
  const axiosError = error as AxiosError<ReportingApiErrorEnvelope>;
  const status = axiosError.response?.status;
  const body = axiosError.response?.data;
  const code = body?.error?.code;
  const message =
    body?.error?.message ||
    axiosError.message ||
    'Reporting API request failed';
  return { status, message, code };
};

export async function reportingApiV1Request<T>(
  config: AxiosRequestConfig
): Promise<T> {
  const access = getAccessToken();
  const response = await axios({
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(access ? { Authorization: `Bearer ${access}` } : {}),
    },
  });
  return response.data as T;
}

export async function fetchReportingApiRows(
  query: ReportingApiReportQuery
): Promise<ReportingApiRow[]> {
  const { extClientId, ...body } = query;
  try {
    const envelope = await reportingApiV1Request<ReportingApiSuccessEnvelope<ReportingApiRow[]>>({
      method: 'POST',
      url: `${getReportingApiV1BaseUrl()}/reports`,
      data: {
        dataset: body.dataset ?? 'performance',
        dimensions: body.dimensions ?? [],
        metrics: body.metrics ?? [],
        filters: body.filters ?? {},
        start_date: body.start_date,
        end_date: body.end_date,
        ...(body.sort?.length ? { sort: body.sort } : {}),
        ...(typeof body.limit === 'number' ? { limit: body.limit } : {}),
        ...(body.cursor ? { cursor: body.cursor } : {}),
      },
      params: extClientId ? { ext_client_id: extClientId } : undefined,
    });
    return Array.isArray(envelope.data) ? envelope.data : [];
  } catch (error) {
    throw toRequestError(error);
  }
}

export async function fetchReportingApiMetaAccounts(
  extClientId?: string
): Promise<import('./reporting-api-v1.types').ReportingApiMetaAccount[]> {
  try {
    const envelope = await reportingApiV1Request<
    ReportingApiSuccessEnvelope<import('./reporting-api-v1.types').ReportingApiMetaAccount[]>
    >({
        method: 'GET',
        url: `${getReportingApiV1BaseUrl()}/meta/accounts`,
        params: extClientId ? { ext_client_id: extClientId } : undefined,
      });
    return Array.isArray(envelope.data) ? envelope.data : [];
  } catch (error) {
    throw toRequestError(error);
  }
}

export async function fetchReportingApiFlights(
  request: ReportingApiV1FlightsRequest
): Promise<ReportingApiV1FlightRow[]> {
  const { extClientId, accountIds, campaignIds, entityLevel, asOf, source } = request;
  try {
    const envelope = await reportingApiV1Request<
    ReportingApiSuccessEnvelope<ReportingApiV1FlightRow[]>
    >({
      method: 'POST',
      url: `${getReportingApiV1BaseUrl()}/reports/flights`,
      data: {
        source,
        account_ids: accountIds.map(String),
        ...(campaignIds?.length ? { campaign_ids: campaignIds.map(String) } : {}),
        ...(entityLevel ? { entity_level: entityLevel } : {}),
        ...(asOf ? { as_of: asOf } : {}),
      },
      params: extClientId ? { ext_client_id: extClientId } : undefined,
    });
    return Array.isArray(envelope.data) ? envelope.data : [];
  } catch (error) {
    throw toRequestError(error);
  }
}
